<script setup>
import CFDisplayOptionsGroup from '@/shared/components/overview-filters/CFDisplayOptionsGroup.vue';
import CFSearchInput from '@/shared/components/CFSearchInput.vue';
import CFUnderlinedButton from '@/shared/components/CFUnderlinedButton.vue';
import CFButton from '@/ui/Button/CFButton.vue';
import CFDropdownButton from '@/shared/components/CFDropdownButton.vue';
import CFFilterSelect from '@/shared/components/overview-filters/CFFilterSelect.vue';
import CFToggle from '@/ui/Toggle/CFToggle.vue';
import { nextTick, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { useSupportStore } from '#modules/support/stores/support.store.js';
import { storeToRefs } from 'pinia';

const supportStore = useSupportStore();
const { searchQuery, sort } = storeToRefs(supportStore);

const showSearch = ref(false);
const searchInputRef = useTemplateRef('searchInputRef');
const localSearchQuery = ref(searchQuery.value);
const localSort = ref(sort.value);
const localOnlyPersonalTickets = ref(supportStore.onlyPersonalTickets);
const localOnlyOpenTickets = ref(supportStore.onlyOpenTickets);

const stopSearchWatch = watchDebounced(
    localSearchQuery,
    (newValue) => {
        searchQuery.value = newValue;
    },
    { debounce: 300 }
);

onBeforeUnmount(() => {
    stopSearchWatch();
});

const toggleSearch = () => {
    if (showSearch.value) {
        localSearchQuery.value = '';
        searchQuery.value = '';
    }

    showSearch.value = !showSearch.value;

    if (showSearch.value) {
        nextTick(() => searchInputRef.value?.$el?.querySelector('input')?.focus());
    }
};

const resetFilters = () => {
    localSort.value = sort.value;
    localOnlyPersonalTickets.value = supportStore.onlyPersonalTickets;
    localOnlyOpenTickets.value = supportStore.onlyOpenTickets;
};

const saveFilters = (close) => {
    sort.value = localSort.value;
    supportStore.onlyPersonalTickets = localOnlyPersonalTickets.value;
    supportStore.onlyOpenTickets = localOnlyOpenTickets.value;
    close();
};
</script>

<template>
    <div class="border-b border-black py-4">
        <div class="-mx-3 flex items-center justify-between gap-1">
            <CFUnderlinedButton
                :text="showSearch ? $t('support.close_search') : $t('support.search')"
                :icon="showSearch ? 'remove-circle' : 'search'"
                @click="toggleSearch"
            />
            <CFDropdownButton
                :text="$t('support.sort_and_filter')"
                icon="arrow-down1"
                @closed="resetFilters"
            >
                <template #default="{ close }">
                    <div class="mb-4 text-base font-bold">{{ $t('support.sort_and_filter') }}</div>

                    <CFDisplayOptionsGroup
                        class="mb-4"
                        :label="$t('general.sort_overview_by')"
                    >
                        <CFFilterSelect
                            v-model="localSort"
                            name="sort"
                        >
                            <option value="-updated_at">{{ $t('support.sort_by_last_activity') }}</option>
                            <option value="-created_at">{{ $t('support.sort_by_creation_date') }}</option>
                        </CFFilterSelect>
                    </CFDisplayOptionsGroup>

                    <div class="flex flex-col divide-y divide-gray-200 border-t border-gray-200">
                        <div class="flex items-center justify-between gap-2 py-4 text-sm">
                            <span>{{ $t('support.show_only_your_tickets') }}</span>
                            <CFToggle v-model="localOnlyPersonalTickets" />
                        </div>
                        <div class="flex items-center justify-between gap-2 py-4 text-sm">
                            <span>{{ $t('support.show_only_open_tickets') }}</span>
                            <CFToggle v-model="localOnlyOpenTickets" />
                        </div>
                    </div>

                    <div class="border-primary mt-4 flex flex-row justify-between border-t pt-5">
                        <CFButton
                            color="primary"
                            variant="outline"
                            @click="close"
                        >
                            {{ $t('button.cancel') }}
                        </CFButton>

                        <CFButton
                            color="primary"
                            size="md"
                            @click="saveFilters(close)"
                        >
                            {{ $t('button.save') }}
                        </CFButton>
                    </div>
                </template>
            </CFDropdownButton>
        </div>
        <Transition name="fade-down">
            <div
                v-show="showSearch"
                class="pt-4"
            >
                <CFSearchInput
                    ref="searchInputRef"
                    v-model="localSearchQuery"
                    :label="$t('support.search_subject_or_number')"
                    class="filter-search"
                />
            </div>
        </Transition>
    </div>
</template>
